import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const sid = () => {
  let s = localStorage.getItem("taven_ai_sid");
  if (!s) { s = "sess-" + Math.random().toString(36).slice(2); localStorage.setItem("taven_ai_sid", s); }
  return s;
};

const SUGGESTIONS = ["How do I become an affiliate?", "How do I sell on TAVEN?", "Tell me about the Education Hub", "How does shipping work?"];

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: "Hi! I'm the TAVEN Assistant. Ask me about products, shipping, the affiliate program, selling, courses, or certificates." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const session = useRef(sid());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: session.current, message: msg, mode: "assistant" });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again or visit our Contact page." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="ai-assistant-toggle"
        className="fixed z-[55] bottom-20 right-4 lg:bottom-6 lg:right-6 w-14 h-14 rounded-full bg-taven-orange text-white shadow-2xl shadow-orange-500/30 flex items-center justify-center hover:bg-[#d9561a] transition-colors">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div className="fixed z-[55] bottom-36 right-4 lg:bottom-24 lg:right-6 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white border border-black/10 shadow-2xl flex flex-col" data-testid="ai-assistant-panel">
          <div className="bg-taven-black text-white p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-taven-gold/20 flex items-center justify-center"><Sparkles className="w-4 h-4 text-taven-gold" /></div>
            <div><p className="font-black font-display leading-none">TAVEN Assistant</p><p className="text-[10px] text-taven-gold uppercase tracking-widest mt-1">Always here to help</p></div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-taven-black text-white" : "bg-taven-gray text-black"}`}>{m.content}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-taven-gray px-3.5 py-2.5 text-sm text-muted-foreground">Thinking…</div></div>}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="text-xs border border-black/15 px-3 py-1.5 hover:border-taven-gold hover:text-taven-gold">{s}</button>)}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-black/10 flex items-center gap-2">
            <input data-testid="ai-assistant-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything…" className="flex-1 bg-taven-gray px-3 py-2.5 text-sm outline-none" />
            <button type="submit" data-testid="ai-assistant-send" disabled={loading} className="bg-taven-orange text-white p-2.5 hover:bg-[#d9561a] disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
